import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import MetricCard from "@/components/MetricCard";
import ResponseModal from "@/components/ResponseModal";
import { BvsTrendChart, SovBarChart, SentimentTrendChart } from "@/components/Charts";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import Onboarding from "@/pages/Onboarding";
import { PlayCircle, ArrowRight, FileStack, Wand2, AlertOctagon, Globe } from "lucide-react";

export default function Dashboard() {
  const { projects, current, selectProject, loading } = useProject();
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [sentimentWeeks, setSentimentWeeks] = useState([]);
  const [geoGaps, setGeoGaps] = useState(null);
  const [running, setRunning] = useState(false);
  const [selectedNeg, setSelectedNeg] = useState(null);

  useEffect(() => {
    if (!current) return;
    (async () => {
      const [d, r, w, s, g] = await Promise.all([
        api.get(`/projects/${current.project_id}/dashboard`),
        api.get(`/projects/${current.project_id}/runs`),
        api.get("/artefacts/stats/weekly").catch(() => ({ data: null })),
        api
          .get(`/projects/${current.project_id}/sentiment-trend?weeks=12`)
          .catch(() => ({ data: [] })),
        api
          .get(`/projects/${current.project_id}/geo-gaps`)
          .catch(() => ({ data: null })),
      ]);
      setData(d.data);
      setRuns(r.data);
      setWeekly(w.data);
      setSentimentWeeks(s.data || []);
      setGeoGaps(g.data);
    })();
  }, [current]);

  const triggerRun = async () => {
    if (!current) return;
    setRunning(true);
    try {
      await api.post(`/projects/${current.project_id}/runs`, {
        engines: ["openai", "anthropic", "gemini"],
      });
      // poll for ~25s to give run a chance to complete
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 2200));
        const r = await api.get(`/projects/${current.project_id}/runs`);
        setRuns(r.data);
        if (r.data[0]?.status === "completed") {
          const d = await api.get(`/projects/${current.project_id}/dashboard`);
          setData(d.data);
          break;
        }
      }
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="mono text-xs uppercase tracking-[0.2em] text-zinc-500">
          Loading workspace…
        </div>
      </div>
    );
  }

  if (!current) return <Onboarding />;

  const latest = data?.latest_run;
  const trend = (data?.trend || []).map((t, i) => ({
    label: `#${i + 1}`,
    bvs: t.bvs,
  }));

  const sovEntries = latest
    ? Object.entries(latest.sov_breakdown || {}).map(([name, value]) => ({
        name,
        value,
      }))
    : [];

  return (
    <DashboardShell
      project={current}
      projects={projects}
      onProjectChange={selectProject}
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="label-eyebrow">Overview</div>
          <h1 className="display mt-2 text-4xl font-black tracking-tighter">
            {current.name}
            <span className="text-zinc-400"> · </span>
            <span className="text-zinc-500">Brand Visibility</span>
          </h1>
          {current.domain && (
            <div className="mt-1 mono text-xs text-zinc-500">{current.domain}</div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/prompts"
            data-testid="btn-manage-prompts"
            className="text-sm border border-zinc-900 px-3.5 py-2 rounded-[4px] hover:bg-zinc-100"
          >
            Manage prompts
          </Link>
          <button
            onClick={triggerRun}
            disabled={running}
            data-testid="btn-run-now"
            className="inline-flex items-center gap-1.5 bg-black text-white text-sm px-4 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            {running ? (
              <>
                <span className="w-3 h-3 border border-white/40 border-t-white rounded-full spin" />
                Running…
              </>
            ) : (
              <>
                <PlayCircle size={14} /> Run now
              </>
            )}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 reveal">
        <MetricCard
          label="Brand Visibility Score"
          value={latest ? latest.bvs_score.toFixed(1) : "—"}
          suffix="/100"
          helper={latest ? `Run ${new Date(latest.created_at).toLocaleDateString()}` : "Run to populate"}
          testId="kpi-bvs"
        />
        <MetricCard
          label="Mention Rate"
          value={latest ? `${latest.mention_rate}%` : "—"}
          helper="Answers that name your brand"
          testId="kpi-mention"
        />
        <MetricCard
          label="Citation Rate"
          value={latest ? `${latest.citation_rate}%` : "—"}
          helper="Mentions with a URL nearby"
          testId="kpi-citation"
        />
        <MetricCard
          label="Engines"
          value={latest ? latest.engines?.length || 0 : 0}
          helper="Running in parallel"
          testId="kpi-engines"
        />
      </div>

      {/* Negative mentions alert */}
      {data?.negative_mentions?.length > 0 && (
        <div
          className="mt-6 border border-rose-200 bg-rose-50 rounded-[6px] p-4"
          data-testid="negative-mentions-banner"
        >
          <div className="flex items-start gap-3">
            <AlertOctagon size={16} className="text-rose-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium text-rose-800">
                {data.negative_mentions.length} negative mention
                {data.negative_mentions.length === 1 ? "" : "s"} detected in the
                latest run
              </div>
              <ul className="mt-2 space-y-1">
                {data.negative_mentions.slice(0, 3).map((n) => (
                  <li
                    key={n.result_id}
                    className="flex items-center justify-between gap-3 text-xs text-rose-700"
                  >
                    <span className="truncate">
                      <span className="mono uppercase tracking-[0.14em] mr-2">
                        {n.engine}
                      </span>
                      {n.prompt_text}
                    </span>
                    <button
                      onClick={() => setSelectedNeg(n)}
                      data-testid={`negative-open-${n.result_id}`}
                      className="underline shrink-0"
                    >
                      Why is the AI saying this about us?
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        <section className="card-flat p-5 lg:col-span-7" data-testid="bvs-trend-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-eyebrow">BVS Trend</div>
              <div className="mt-1 text-sm text-zinc-500">
                Last {trend.length || 0} runs
              </div>
            </div>
            <span className="pill">
              <span className="dot" /> Composite score
            </span>
          </div>
          <div className="mt-4">
            {trend.length ? (
              <BvsTrendChart data={trend} />
            ) : (
              <EmptyBlock text="No runs yet — trigger a run to see your BVS timeline." />
            )}
          </div>
        </section>

        <section className="card-flat p-5 lg:col-span-5" data-testid="sov-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-eyebrow">Share of Voice</div>
              <div className="mt-1 text-sm text-zinc-500">Mention distribution</div>
            </div>
            <span className="pill mono text-[10px]">Latest run</span>
          </div>
          <div className="mt-4">
            {sovEntries.length ? (
              <SovBarChart data={sovEntries} brand={current.name} />
            ) : (
              <EmptyBlock text="SOV populates after your first run." />
            )}
          </div>
        </section>
      </div>

      {/* Geo Gaps insight */}
      {geoGaps?.has_gap && (
        <section
          className="mt-6 card-flat p-5"
          data-testid="geo-gaps-card"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-[4px] border border-zinc-200 flex items-center justify-center shrink-0">
                <Globe size={15} />
              </div>
              <div>
                <div className="label-eyebrow">Geo gap detected</div>
                <div className="mt-1 text-sm text-zinc-800">
                  You're mentioned in{" "}
                  <strong className="mono">{geoGaps.base_rate}%</strong> of{" "}
                  <span className="mono">{geoGaps.base_language}</span> prompts
                  but only{" "}
                  <strong className="text-rose-700 mono">
                    {geoGaps.weak_rate}%
                  </strong>{" "}
                  of <span className="mono">{geoGaps.weak_language}</span>{" "}
                  prompts.
                </div>
              </div>
            </div>
            <Link
              to="/dashboard/geo"
              data-testid="geo-gaps-open"
              className="text-xs border border-zinc-900 px-3 py-1.5 rounded-[4px] hover:bg-zinc-100 transition-colors"
            >
              Open matrix <ArrowRight size={12} className="inline -mt-0.5 ml-1" />
            </Link>
          </div>
          {geoGaps.weak_prompts?.length > 0 && (
            <div className="mt-4 border-t border-zinc-200 pt-4">
              <div className="label-eyebrow">
                Consider {langLabelShort(geoGaps.weak_language)} content for
              </div>
              <ul className="mt-2 space-y-1">
                {geoGaps.weak_prompts.map((p) => (
                  <li
                    key={p.prompt_id}
                    className="text-sm text-zinc-700 flex items-start gap-2"
                    data-testid={`weak-prompt-${p.prompt_id}`}
                  >
                    <span className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 shrink-0 w-20 pt-0.5">
                      {p.category.replace("_", " ")}
                    </span>
                    <span>{p.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Sentiment Trend */}
      <section
        className="mt-6 card-flat p-5"
        data-testid="sentiment-trend-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-eyebrow">Sentiment trend · 12 weeks</div>
            <div className="mt-1 text-sm text-zinc-500">
              Distribution of positive / neutral / negative mentions over time.
            </div>
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <LegendDot color="#10b981" label="Positive" />
            <LegendDot color="#a1a1aa" label="Neutral" />
            <LegendDot color="#e53935" label="Negative" />
          </div>
        </div>
        <div className="mt-4">
          {sentimentWeeks.length ? (
            <SentimentTrendChart data={sentimentWeeks} />
          ) : (
            <div className="h-[160px] flex items-center justify-center mini-grid-bg border border-dashed border-zinc-200 rounded-[4px] text-xs text-zinc-500">
              No sentiment data yet — run a scan to populate this chart.
            </div>
          )}
        </div>
      </section>

      {/* Generated-this-week widget */}
      {weekly && (
        <section
          className="mt-6 card-flat p-5"
          data-testid="weekly-artefacts-widget"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-[4px] border border-zinc-200 flex items-center justify-center">
                <FileStack size={14} />
              </div>
              <div>
                <div className="label-eyebrow">Generated this week</div>
                <div className="mt-1 text-sm text-zinc-500">
                  Every "Fix this" click lands here for copy-paste reuse.
                </div>
              </div>
            </div>
            <Link
              to="/dashboard/artefacts"
              data-testid="view-artefacts"
              className="text-xs mono uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
            >
              View all <ArrowRight size={12} />
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <WeekStat label="Generated" value={weekly.generated_count} />
            <WeekStat
              label="Completed"
              value={weekly.completed_count}
              tone="emerald"
            />
            <WeekStat
              label="FAQ schemas"
              value={weekly.by_type?.missing_faq_schema || 0}
            />
            <WeekStat
              label="Rewrites"
              value={weekly.by_type?.not_answer_first || 0}
            />
          </div>
          {weekly.recent && weekly.recent.length > 0 && (
            <div className="mt-5 border-t border-zinc-200 pt-4">
              <div className="label-eyebrow">Recent</div>
              <ul className="mt-2 divide-y divide-zinc-100">
                {weekly.recent.slice(0, 3).map((r) => (
                  <li
                    key={r.artefact_id}
                    className="py-2 flex items-start gap-3 text-sm"
                  >
                    <Wand2 size={12} className="mt-1 text-zinc-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-zinc-800">{r.title}</div>
                      <div className="mono text-[11px] text-zinc-500">
                        {new Date(r.created_at).toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Recent runs */}
      <section className="mt-6 card-flat" data-testid="recent-runs">
        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Recent Runs</div>
            <div className="mt-1 text-sm text-zinc-500">
              Weekly prompt tracking across models
            </div>
          </div>
          <Link
            to="/dashboard/runs"
            data-testid="view-all-runs"
            className="text-xs mono uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
          >
            View all <ArrowRight size={12} />
          </Link>
        </div>
        <div className="divide-y divide-zinc-200">
          {runs.slice(0, 5).map((r) => (
            <div
              key={r.run_id}
              className="p-5 grid grid-cols-12 gap-4 items-center text-sm"
            >
              <div className="col-span-4 mono text-xs text-zinc-500">
                {new Date(r.created_at).toLocaleString()}
              </div>
              <div className="col-span-2">
                <Badge status={r.status} />
              </div>
              <div className="col-span-3 text-zinc-700">
                {r.engines?.length || 0} engines · {r.results_count || 0} results
              </div>
              <div className="col-span-3 text-right mono tabular-nums">
                BVS {r.bvs_score?.toFixed(1) ?? "—"}
              </div>
            </div>
          ))}
          {!runs.length && (
            <div className="p-8 text-sm text-zinc-500 text-center">
              No runs yet. Hit <strong>Run now</strong> above to start.
            </div>
          )}
        </div>
      </section>

      <ResponseModal response={selectedNeg} onClose={() => setSelectedNeg(null)} />
    </DashboardShell>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-zinc-600">
      <span
        aria-hidden
        className="w-2 h-2 rounded-full"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

function langLabelShort(code) {
  return (
    {
      "en-GB": "British English",
      "en-US": "American English",
      "de-DE": "German",
      "fr-FR": "French",
      "es-ES": "Spanish",
      "nl-NL": "Dutch",
      "it-IT": "Italian",
    }[code] || code
  );
}

function EmptyBlock({ text }) {
  return (
    <div className="h-[220px] flex items-center justify-center mini-grid-bg border border-dashed border-zinc-200 rounded-[4px] text-xs text-zinc-500">
      {text}
    </div>
  );
}

function Badge({ status }) {
  const map = {
    running: ["bg-amber-50 text-amber-700 border-amber-200", "Running"],
    completed: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Completed"],
    failed: ["bg-rose-50 text-rose-700 border-rose-200", "Failed"],
  };
  const [cls, label] = map[status] || ["bg-zinc-50 text-zinc-700 border-zinc-200", status];
  return (
    <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${cls}`}>
      {label}
    </span>
  );
}

function WeekStat({ label, value, tone }) {
  const cls =
    tone === "emerald" ? "text-emerald-700" : "text-zinc-900";
  return (
    <div className="border border-zinc-200 rounded-[4px] p-3">
      <div className="label-eyebrow">{label}</div>
      <div className={`display text-2xl font-black mt-1 tabular-nums ${cls}`}>
        {value}
      </div>
    </div>
  );
}
